import { selectProjectUsers } from "@/store/project-selector";
import { useAppSelector } from "@/store/store-hooks";
import {
  ArrowDownIcon,
  blue,
  FaroChipTag,
  FaroText,
  grey,
  TeamIcon,
  UserAvatar,
} from "@faro-lotv/flat-ui";
import { mayBeEmail } from "@faro-lotv/foundation";
import { GenericUserInfo } from "@faro-lotv/service-wires";
import { alpha, Autocomplete, Stack, TextField } from "@mui/material";
import { isEqual } from "es-toolkit";
import { useCallback, useEffect, useMemo, useState, type JSX } from "react";

type UsersInviteProps = {
  onEmailsChanged(emails: string[]): void;
};

/** @returns A combobox to invite users to the snapshot */
export function UsersInvite({
  onEmailsChanged,
}: UsersInviteProps): JSX.Element {
  const [addedUsers, setAddedUsers] = useState<GenericUserInfo[]>([]);

  const [text, setText] = useState("");

  const clearUsers = useCallback(() => setAddedUsers([]), []);
  const updateUsers = useCallback((value: GenericUserInfo) => {
    if (mayBeEmail(value.email)) {
      setAddedUsers((pre) => {
        if (pre.includes(value)) {
          return [...pre];
        }
        return [...pre, value];
      });
      setText("");
    }
  }, []);
  const removeUser = useCallback((value: GenericUserInfo) => {
    setAddedUsers((pre) => {
      const index = pre.findIndex((a) => a === value);
      if (index >= 0) {
        const res = [...pre];
        res.splice(index, 1);
        return res;
      }
      return [...pre];
    });
  }, []);
  const onKeyPressed = useCallback(
    (event: React.KeyboardEvent) => {
      if (event.key === "Enter") {
        updateUsers(createDummyUserInfo(text));
      }
    },
    [text, updateUsers],
  );

  useEffect(() => {
    const emails = addedUsers.map((user) => user.email);
    // A valid email still typed in the input counts as entered,
    // so the user is not forced to confirm it with Enter
    if (mayBeEmail(text) && !emails.includes(text)) {
      emails.push(text);
    }
    onEmailsChanged(emails);
  }, [addedUsers, text, onEmailsChanged]);

  const projectUsers = useAppSelector(selectProjectUsers, isEqual);
  const options = useMemo(() => {
    const [teams, users] = projectUsers.reduce<
      [GenericUserInfo[], GenericUserInfo[]]
    >(
      (acc, user) => {
        if (user.authorityType === "UserGroupRef") {
          acc[0].push(user);
        } else {
          acc[1].push(user);
        }
        return acc;
      },
      [[], []],
    );
    return [
      ...teams.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
      ...users.sort((a, b) => (a.name ?? "").localeCompare(b.name ?? "")),
    ];
  }, [projectUsers]);

  return (
    <Stack sx={{ gap: 1 }}>
      <FaroText variant="heading12">Invite users</FaroText>
      <Autocomplete
        value={addedUsers}
        multiple
        options={options}
        getOptionLabel={(option) =>
          typeof option === "string" ? option : option.email
        }
        filterOptions={(options, state) =>
          options.filter((o) =>
            (o.name + o.email)
              .toLowerCase()
              .includes(state.inputValue.toLowerCase()),
          )
        }
        inputValue={text}
        onInputChange={(event, currentText) => {
          let value = currentText;
          if (value.includes(",") || value.includes(" ")) {
            value = currentText.substring(0, currentText.length - 1);
            updateUsers(createDummyUserInfo(value));
          } else {
            setText(value);
          }
        }}
        onChange={(event, value, reason) => {
          const currentValue = value[value.length - 1];
          if (reason === "selectOption") {
            updateUsers(
              typeof currentValue === "string"
                ? createDummyUserInfo(currentValue)
                : currentValue,
            );
          } else if (reason === "clear") {
            clearUsers();
          }
        }}
        renderOption={({ style, ...props }, user) => {
          const isGroup = user.authorityType === "UserGroupRef";
          return (
            <li
              {...props}
              style={{ ...style, fontSize: "0.875rem" }}
              key={user.email}
            >
              <UserAvatar
                userDisplayInfo={user}
                size="xs"
                shouldShowWhiteRim={false}
                displayOption={isGroup ? "nameOnly" : "nameAndEmail"}
                icon={isGroup ? <TeamIcon sx={{ width: "16px" }} /> : undefined}
              />
            </li>
          );
        }}
        freeSolo
        onKeyDown={onKeyPressed}
        popupIcon={<ArrowDownIcon />}
        renderInput={({ ...props }) => (
          <TextField
            {...props}
            variant="outlined"
            placeholder="Email(s), comma separated"
          />
        )}
        renderValue={(tagValue) =>
          tagValue.map((option, index) => {
            const user =
              typeof option === "string" ? createDummyUserInfo(option) : option;
            return (
              <FaroChipTag
                key={`chip-${index}`}
                label={user.email}
                onDelete={() => removeUser(user)}
              />
            );
          })
        }
        slotProps={{
          paper: {
            sx: {
              "& .MuiAutocomplete-listbox": {
                "& .MuiAutocomplete-option": {
                  backgroundColor: grey[0],
                  color: grey[800],
                  ":hover": {
                    color: blue[500],
                    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                    background: alpha(grey[500], 0.15),
                  },
                  '&[aria-selected="true"]': {
                    color: blue[500],
                    // eslint-disable-next-line @typescript-eslint/no-magic-numbers
                    background: alpha(grey[500], 0.25),
                  },
                },
              },
            },
          },
        }}
        sx={{
          "& .MuiOutlinedInput-root": { paddingY: "2px" },
        }}
      />
    </Stack>
  );
}

function createDummyUserInfo(email: string): GenericUserInfo {
  return {
    email,
    identity: email,
    authorityType: "UserInviteRef",
  };
}
